import type { ImageGenerationConfirmation } from '@/lib/assistant/image-generation-confirmation';
import type { BlogWizardTurnMetadata } from '@/lib/site-assistant/blog/wizard-context';
import type { CreditNotice } from '@/types/shared';

export interface Conversation {
  id: string;
  title: string;
  status?: string;
  updatedAt: number | Date;
  messageCount: number;
}

export type AssistantSuggestion = string;

type ProposalStatus = 'pending' | 'generating' | 'accepted' | 'rejected' | 'expired';

export interface Message {
  role: 'user' | 'assistant' | 'tool' | 'credit_notice';
  content: string;
  timestamp: number;
  fromHistory?: boolean;
  imageConfirmation?: ImageGenerationConfirmation;
  creditNotice?: CreditNotice;
  toolCallId?: string;
  toolName?: string;
  toolInput?: Record<string, unknown>;
  toolArgsAccumulated?: string;
  toolResult?: {
    rawOutput?: unknown;
  };
  toolError?: string;
  backgroundTaskStatus?: 'running' | 'completed' | 'failed';
  backgroundTaskProgressMessage?: string;
  streaming?: boolean;
  // Locally generated (e.g. blog wizard narration), not produced by the model.
  synthetic?: boolean;
  // TODO: display-only; tool-ui.ts reads moves for the progress label. Could be removed
  // if tool-ui.ts reads from sectionDirectApplyMetadata instead.
  sectionsRepositionedMetadata?: {
    pageId: string;
    moves: Array<{
      sectionId: string;
      targetRowPosition: number;
    }>;
  };
  sectionProposalMetadata?: {
    pageId: string;
    proposal: {
      id: string;
      batchId: string;
      pageId: string;
      pageVersionId?: string;
      operation: 'add' | 'update' | 'delete' | 'replace' | 'reorder';
      targetSectionId?: string;
      payload: Record<string, unknown>;
      beforeSnapshot?: Record<string, unknown>;
      status: ProposalStatus;
      position: number;
    };
  };
  sectionProposalBatchMetadata?: {
    proposals: NonNullable<Message['sectionProposalMetadata']>[];
  };
  proposalRejectedMetadata?: {
    proposalId: string;
  };
  // Emitted by auto-apply operations (e.g. reorder) that bypass the proposal system.
  // The frontend applies the change directly via the editor store + autosave.
  // Operation-specific fields are flattened alongside `operation` — each sync hook
  // reads the fields relevant to the operation it handles.
  sectionDirectApplyMetadata?: {
    pageId: string;
    operation: string;
    [key: string]: unknown;
  };
  blogWizardTurnMetadata?: BlogWizardTurnMetadata;
  blogTopicsReadyMetadata?: {
    pageId: string;
    practiceArea: string | null;
    topicOptions: string[];
  };
}

export type MessageKeyEntry = {
  message: Message;
  key: string;
};

export enum TaskType {
  REPHRASE = 'rephrase',
  SUMMARIZE = 'summarize',
  SHORTEN = 'shorten',
  EXTEND = 'extend',
  FIX_GRAMMAR = 'fixGrammar',
  CONTINUE_WRITING = 'continueWriting',
  CUSTOM = 'custom',
}
